"use client";

import { useCallback, useEffect, useState } from "react";
import {
  Copy,
  Share2,
  Award,
  Users,
  QrCode,
  CheckCircle,
  Scan,
  X,
} from "lucide-react";
import QRCode from "react-qr-code";
import QrReader from "react-qr-scanner";
import { useSelector } from "react-redux";
import {
  getreferralKey,
  selectSiteDomainName,
} from "../../../AsianExchangeSrc/utils/helper/commonSelectors";
import { getAffiliateData } from "./../../../AsianExchangeSrc/api/index";

export default function Referral() {
  const referralKey = useSelector(getreferralKey());
  const siteName = useSelector(selectSiteDomainName());
  const [referralCode] = useState(referralKey);
  const [referralLink] = useState(() => {
    const baseUrl = siteName.startsWith("http")
      ? siteName
      : `https://${siteName}`;
    return new URL(`/signUp?signUp=true&referral=${referralKey}`, baseUrl).href;
  });
  const [currentUserId, setCurrentUserId] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [scannedCode, setScannedCode] = useState("");
  const [activeTab, setActiveTab] = useState("code");
  const [toast, setToast] = useState({
    visible: false,
    title: "",
    description: "",
  });
  const [affiliateData, setAffiliateData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({
    totalReferrals: 0,
    activeReferrals: 0,
    totalEarnings: 0,
    nonDepositedUsers: 0,
  });
  const [showNonDepositedPopup, setShowNonDepositedPopup] = useState(false);

  const fetchAffiliateData = useCallback(
    async (
      page = 1,
      limit = 10,
      search = "",
      roles = [],
      status = "active",
      userId
    ) => {
      setIsLoading(true);
      try {
        const response = await getAffiliateData(
          page,
          limit,
          search,
          roles,
          status,
          userId || currentUserId
        );
        const users = response.data?.users || [];
        setAffiliateData(users);

        const activeReferrals = users?.filter(
          (user) => user.status === "active"
        )?.length;
        const totalEarnings = users?.reduce(
          (sum, user) => sum + (user.referralRewardEarned || 0),
          0
        );
        const nonDepositedUsers = users?.filter(
          (user) => !user.hasMadeFirstDeposit
        )?.length;

        setStats({
          totalReferrals: users?.length,
          activeReferrals,
          totalEarnings,
          nonDepositedUsers,
        });
      } catch (error) {
        console.error("Error fetching affiliate data:", error);
        showToast("Error", "Failed to load referral data");
      } finally {
        setIsLoading(false);
      }
    },
    [currentUserId]
  );

  useEffect(() => {
    fetchAffiliateData();
  }, [fetchAffiliateData, currentUserId]);

  const refreshData = () => {
    setCurrentUserId("");
    fetchAffiliateData();
  };

  const showToast = (title, description) => {
    setToast({ visible: true, title, description });
    setTimeout(() => {
      setToast((prev) => ({ ...prev, visible: false }));
    }, 3000);
  };

  const handleUserClick = (userId) => {
    setCurrentUserId(userId);
  };

  const copyToClipboard = (text, type = "code") => {
    navigator.clipboard.writeText(text);
    showToast(
      "Copied!",
      type === "code"
        ? "Referral code copied to clipboard"
        : "Referral link copied to clipboard"
    );
  };

  const shareReferral = async () => {
    if (navigator.share) {
      try {
        const baseUrl = siteName.startsWith("http")
          ? siteName
          : `https://${siteName}`;
        const fullUrl = new URL(referralLink, baseUrl).href;

        await navigator.share({
          title: "Join me on our platform!",
          text: "Use my referral code to get 25% off your first month!",
          url: fullUrl,
        });
        showToast("Shared!", "Referral successfully shared");
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error sharing:", error);
          showToast("Share Failed", "Could not share referral");
        }
      }
    } else {
      copyToClipboard(referralLink, "link");
    }
  };

  const handleScan = (result) => {
    if (result) {
      const scanned = result.text || result;
      setScannedCode(scanned);
      showToast("QR Code Scanned!", `Code: ${scanned}`);
      setShowScanner(false);
    }
  };

  const handleScanError = (error) => {
    console.error("Scan error:", error);
    showToast("Scan Error", "Could not scan QR code");
  };

  const handleCallUser = (mobile) => {
    window.location.href = `tel:${mobile}`;
  };

  const referrals = affiliateData.map((user) => ({
    id: user._id,
    name: user.name,
    email: user.mobile ? `${user.mobile}@example.com` : "N/A",
    date: user.createdAt,
    status: user.status,
    firstDeposit: user.firstAccountStatement?.deposit || "N/A",
    rewardAmount: user.referralRewardEarned || 0,
  }));

  const nonDepositedUsers = affiliateData
    ?.filter((user) => !user.hasMadeFirstDeposit)
    .map((user) => ({
      id: user._id,
      userName: user.userName,
      mobile: user.mobile,
    }));

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      {toast.visible && (
        <div className="fixed top-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 max-w-xs animate-in fade-in slide-in-from-top-5 duration-300">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green mr-2" />
            <h4 className="font-medium text-gray-800">{toast.title}</h4>
          </div>
          <p className="text-gray-600 text-sm mt-1">{toast.description}</p>
        </div>
      )}

      {showNonDepositedPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                Users Yet to Deposit
              </h3>
              <button
                onClick={() => setShowNonDepositedPopup(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            {nonDepositedUsers?.length > 0 ? (
              <div className="space-y-4">
                {nonDepositedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-gray-800">
                          Username: {user.userName}
                        </p>
                        <p className="text-sm text-gray-600">
                          Mobile: {user.mobile}
                        </p>
                      </div>
                      <button
                        onClick={() => handleCallUser(user.mobile)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                      >
                        Call User
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-center">
                All users have made their first deposit.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold mb-2 text-gray-900">
            Refer & Earn Rewards
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Share your unique code with friends and earn rewards when they join.
            Your friends get 25% off their first month!
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Referrals</p>
                <p className="text-2xl font-bold">{stats.totalReferrals}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Referrals</p>
                <p className="text-2xl font-bold">{stats.activeReferrals}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center mr-3">
                <Award className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Earnings</p>
                <p className="text-2xl font-bold">
                  ₹{stats?.totalEarnings?.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
          <div
            className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setShowNonDepositedPopup(true)}
          >
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                <Users className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Non-Deposited Users</p>
                <p className="text-2xl font-bold">{stats.nonDepositedUsers}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6">
            <div className="w-full">
              <div className="flex rounded-lg bg-white border border-gray-200 p-1 mb-4 shadow-sm">
                <button
                  onClick={() => setActiveTab("code")}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    activeTab === "code"
                      ? "bg-purple-100 text-purple-700"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  Referral Code
                </button>
                <button
                  onClick={() => setActiveTab("qr")}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    activeTab === "qr"
                      ? "bg-purple-100 text-purple-700"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  QR Code
                </button>
              </div>

              {activeTab === "code" && (
                <div className="space-y-4">
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                    <div className="p-4 border-b border-gray-200">
                      <div className="flex items-center">
                        <Copy className="mr-2 h-5 w-5 text-purple-600" />
                        <h3 className="font-semibold text-lg text-gray-900">
                          Your Referral Code
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Share this code with friends to give them 25% off
                      </p>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between p-4 bg-gray-100 rounded-lg">
                        <span className="text-xl font-mono font-bold text-gray-800">
                          {referralCode}
                        </span>
                        <button
                          onClick={() => copyToClipboard(referralCode, "code")}
                          className="px-3 py-1 bg-white border border-gray-300 hover:bg-gray-50 rounded-md flex items-center text-sm transition-colors text-gray-700"
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Copy
                        </button>
                      </div>

                      <div className="mt-6">
                        <h4 className="font-medium text-gray-800 mb-2">
                          Share your referral link
                        </h4>
                        <div className="flex items-center p-3 bg-gray-100 rounded-lg overflow-hidden">
                          <div className="truncate text-sm text-gray-600 font-mono flex-1">
                            {referralLink}
                          </div>
                          <button
                            onClick={() => copyToClipboard(referralLink, "link")}
                            className="ml-2 p-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-md flex-shrink-0"
                            aria-label="Copy link"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-4">
                        <button
                          onClick={shareReferral}
                          className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-md flex items-center justify-center font-medium transition-colors"
                        >
                          <Share2 className="h-5 w-5 mr-2" />
                          Share with Friends
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="font-semibold text-lg text-gray-900">
                        How It Works
                      </h3>
                    </div>
                    <div className="p-4">
                      <div className="space-y-4">
                        <div className="flex">
                          <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center mr-3 flex-shrink-0">
                            <span className="text-purple-700 font-medium">
                              1
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">
                              Share your referral code
                            </p>
                            <p className="text-sm text-gray-600">
                              Send your unique code to friends via email, social
                              media, or text
                            </p>
                          </div>
                        </div>
                        <div className="flex">
                          <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center mr-3 flex-shrink-0">
                            <span className="text-purple-700 font-medium">
                              2
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">
                              Friends sign up
                            </p>
                            <p className="text-sm text-gray-600">
                              They enter your code during registration to get
                              25% off
                            </p>
                          </div>
                        </div>
                        <div className="flex">
                          <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center mr-3 flex-shrink-0">
                            <span className="text-purple-700 font-medium">
                              3
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">
                              You earn rewards
                            </p>
                            <p className="text-sm text-gray-600">
                              Get ₹10 for each friend who joins and makes their
                              first deposit
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "qr" && (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center">
                      <QrCode className="mr-2 h-5 w-5 text-purple-600" />
                      <h3 className="font-semibold text-lg text-gray-900">
                        Your Referral QR Code
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Let friends scan this code to join using your referral
                    </p>
                  </div>
                  <div className="p-4 flex flex-col items-center">
                    <div className="bg-white p-4 border border-gray-200 rounded-lg">
                      <QRCode
                        value={
                          siteName.startsWith("http")
                            ? siteName
                            : `https://${siteName}` + referralLink
                        }
                        size={200}
                        level="H"
                        bgColor="#FFFFFF"
                        fgColor="#000000"
                      />
                    </div>

                    {showScanner && (
                      <div className="mt-4 w-full">
                        <div className="p-2 bg-black rounded-lg">
                          <QrReader
                            delay={300}
                            onError={handleScanError}
                            onScan={handleScan}
                            style={{ width: "100%", height: "300px" }}
                            constraints={{ facingMode: "environment" }}
                          />
                        </div>
                        <p className="text-sm text-gray-500 mt-2 text-center">
                          Position the QR code in the center of the camera
                        </p>
                      </div>
                    )}

                    {scannedCode && (
                      <div className="mt-4 p-3 bg-gray-100 border border-gray-200 rounded-lg w-full">
                        <p className="font-medium text-gray-800">
                          Scanned Code:
                        </p>
                        <p className="font-mono text-sm text-gray-700 break-all">
                          {String(scannedCode)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden h-full shadow-sm">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Users className="mr-2 h-5 w-5 text-purple-600" />
                    <h3 className="font-semibold text-lg text-gray-900">
                      Your Referrals
                    </h3>
                  </div>
                  <button
                    onClick={refreshData}
                    className="text-sm text-purple-600 hover:text-purple-800"
                  >
                    Refresh
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  People who joined using your code
                </p>
              </div>
              <div className="p-4">
                {isLoading ? (
                  <div className="text-center py-8">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-purple-600 border-r-transparent"></div>
                    <p className="mt-2 text-gray-500">
                      Loading your referrals...
                    </p>
                  </div>
                ) : referrals?.length > 0 ? (
                  <div className="space-y-4">
                    {referrals.map((referral) => (
                      <div
                        key={referral.id}
                        className="p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center text-sm font-medium mr-3 text-purple-700">
                              {referral.name.substring(0, 2).toUpperCase()}
                            </div>
                            <div onClick={() => handleUserClick(referral?.id)}>
                              <p className="font-medium text-gray-900">
                                {referral.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {new Date(referral.date).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              referral.status === "active"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {referral.status}
                          </span>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-gray-50 p-2 rounded">
                            <span className="text-gray-500">
                              First Deposit:
                            </span>
                            <span className="ml-1 font-medium">
                              {referral.firstDeposit}
                            </span>
                          </div>
                          <div className="bg-gray-50 p-2 rounded">
                            <span className="text-gray-500">Your Reward:</span>
                            <span className="ml-1 font-medium">
                              ₹{referral.rewardAmount}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 mx-auto text-gray-300" />
                    <p className="mt-2 text-gray-500">No referrals yet</p>
                    <p className="text-sm text-gray-400 mt-1 mb-4">
                      Share your code to start earning rewards
                    </p>
                    <button
                      className="py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-md flex items-center justify-center mx-auto font-medium transition-colors"
                      onClick={shareReferral}
                    >
                      <Share2 className="mr-2 h-4 w-4" />
                      Invite Friends
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}